import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAxios } from '../../utils/hook/useAxios';

const STATUS_OPTIONS = [
  { value: '1', label: 'Activée', icon: 'bi-check-circle-fill', color: 'text-success' },
  { value: '3', label: 'En pause', icon: 'bi-pause-circle-fill', color: 'text-warning' },
  { value: '2', label: 'Désactivée', icon: 'bi-x-circle-fill', color: 'text-danger' },
];

function Navlist({ vehiculeId, annonces: annoncesFromParent = null }) {
  const [annonces, setAnnonces] = useState(annoncesFromParent ?? []);
  const [option, setOption] = useState(annoncesFromParent?.[0]?.status ?? null);
  const [vehicle, setVehicle] = useState(null);
  const axios = useAxios();
  const location = useLocation();

  /* Fetch docs pour badge */
  useEffect(() => {
    if (!vehiculeId) return;
    axios
      .get('/vehicles/searchbyid/' + vehiculeId)
      .then(({ data }) => setVehicle(data))
      .catch(() => {});
  }, [vehiculeId]);

  const docsCount = vehicle
    ? [
        vehicle.CertificatImmatriculationDoc,
        vehicle.AssuranceDoc,
        vehicle.ControleTechniqueDoc,
      ].filter(Boolean).length
    : null;

  /* N'appelle l'API que si les données ne sont pas fournies par le parent */
  useEffect(() => {
    if (annoncesFromParent !== null) return;
    axios
      .get('/vehicleAnnonces/getbyvehiculeid/' + vehiculeId)
      .then(({ data }) => {
        if (data?.length > 0) {
          setAnnonces(data);
          setOption(data[0].status);
        }
      })
      .catch(() => {});
  }, [vehiculeId, annoncesFromParent]);

  const handleOption = (val) => {
    if (!annonces.length) return;
    setOption(val);
    axios
      .put(`/vehicleAnnonces/${annonces[0].vehiculeAnnonceId}`, { status: val })
      .then(() => toast.success('Annonce mise à jour'))
      .catch(() => {});
  };

  const links = [
    { to: `/account/voiture/${vehiculeId}/calendar/0`, icon: 'bi-calendar3', label: 'Calendrier' },
    {
      to: `/account/voiture/prix-et-reductions/${vehiculeId}`,
      icon: 'bi-tag',
      label: 'Prix et réductions',
    },
    {
      to: `/account/voiture/preferences-de-reservation/${vehiculeId}`,
      icon: 'bi-sliders',
      label: 'Préférences de réservation',
    },
    { to: `/account/your-car/edit-photos/${vehiculeId}`, icon: 'bi-images', label: 'Photos' },
    {
      to: `/account/settings/availabilities/${vehiculeId}`,
      icon: 'bi-clock',
      label: 'Disponibilités',
    },
    {
      to: `/account/settings/vehicule/${vehiculeId}`,
      icon: 'bi-gear',
      label: 'Paramètres du véhicule',
      docsBadge: true,
    },
  ];

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === String(option));

  return (
    <div className="d-flex flex-column gap-3">
      {/* Navigation */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="card-header bg-white border-bottom py-2 px-3">
          <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
            <i className="bi bi-car-front me-2 text-primary"></i>Gestion du véhicule
          </p>
        </div>
        <ul className="list-group list-group-flush">
          {links.map(({ to, icon, label, docsBadge }) => {
            const active = location.pathname === to;
            return (
              <li key={to} className="list-group-item p-0">
                <Link
                  to={to}
                  className={`d-flex align-items-center gap-2 px-3 py-2 text-decoration-none ${active ? 'bg-primary bg-opacity-10 text-primary fw-semibold' : 'text-body-secondary'}`}
                  style={{ fontSize: 13, transition: 'background 0.15s' }}
                >
                  <i className={`bi ${icon}`} style={{ width: 16, textAlign: 'center' }}></i>
                  {label}
                  {docsBadge && docsCount !== null && (
                    <span
                      className={`badge ms-auto rounded-pill ${docsCount === 3 ? 'bg-success' : docsCount > 0 ? 'bg-warning text-dark' : 'bg-danger'}`}
                      style={{ fontSize: 10 }}
                    >
                      {docsCount}/3
                    </span>
                  )}
                  {active && !docsBadge && (
                    <i className="bi bi-chevron-right ms-auto" style={{ fontSize: 10 }}></i>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Emplacements par annonce */}
      {annonces.length > 0 && (
        <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
          <div className="card-header bg-white border-bottom py-2 px-3">
            <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
              <i className="bi bi-geo-alt me-2 text-primary"></i>Emplacements
            </p>
          </div>
          <ul className="list-group list-group-flush">
            {annonces.map((a) => (
              <li key={a.vehiculeAnnonceId} className="list-group-item p-0">
                <a
                  href={`/account/voiture/emplcaments-et-livraison/${vehiculeId}/annonce/${a.vehiculeAnnonceId}`}
                  className="d-flex align-items-center gap-2 px-3 py-2 text-decoration-none text-body-secondary"
                  style={{ fontSize: 13 }}
                >
                  <i className="bi bi-pin-map" style={{ width: 16, textAlign: 'center' }}></i>
                  Annonce #{a.vehiculeAnnonceId}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Statut de l'annonce */}
      <div className="card border-0 shadow-sm rounded-3 overflow-hidden">
        <div className="card-header bg-white border-bottom py-2 px-3">
          <p className="mb-0 fw-semibold" style={{ fontSize: 13 }}>
            <i className="bi bi-toggle-on me-2 text-primary"></i>Statut de l'annonce
          </p>
        </div>
        <div className="card-body p-3">
          {option === '0' ? (
            <div className="alert alert-warning mb-0 py-2" style={{ fontSize: 12 }}>
              <i className="bi bi-hourglass-split me-2"></i>
              En attente de validation par l'administrateur.
            </div>
          ) : (
            <>
              <div className="d-flex flex-column gap-2 mb-3">
                {STATUS_OPTIONS.map(({ value, label, icon, color }) => (
                  <button
                    key={value}
                    className={`btn btn-sm text-start d-flex align-items-center gap-2 ${String(option) === value ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: 12 }}
                    onClick={() => handleOption(value)}
                  >
                    <i className={`bi ${icon} ${String(option) === value ? '' : color}`}></i>
                    {label}
                    {String(option) === value && <i className="bi bi-check2 ms-auto"></i>}
                  </button>
                ))}
              </div>
              {currentStatus && (
                <p className="text-muted mb-0" style={{ fontSize: 11 }}>
                  Statut actuel :{' '}
                  <strong className={currentStatus.color}>{currentStatus.label}</strong>
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Supprimer annonce */}
      {annonces.length > 0 && (
        <>
          <button
            className="btn btn-outline-danger btn-sm w-100"
            data-bs-toggle="modal"
            data-bs-target="#supprimerAnnonceModal"
          >
            <i className="bi bi-trash me-2"></i>Supprimer l'annonce
          </button>

          <div className="modal fade" id="supprimerAnnonceModal" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-bottom">
                  <h6 className="modal-title fw-bold text-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>Supprimer l'annonce ?
                  </h6>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" />
                </div>
                <div className="modal-body">
                  <p className="text-muted small mb-3">
                    Cette action est irréversible. L'annonce sera définitivement supprimée de notre
                    base de données.
                  </p>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary flex-fill" data-bs-dismiss="modal">
                      Annuler
                    </button>
                    <button className="btn btn-danger flex-fill">Confirmer la suppression</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Navlist;
